import "snapsvg-cjs";

/**
 * Simple library for creating horizontal bar-graphs
 * @param container string|DOMElement
 * @constructor
 */
function SdGraph(container) {

	this.container = typeof container === 'string' ? document.querySelector(container) : container;

	this.animationDuration = 1500;

	this.barMarginBottom = 'graphMarginBottom' in this.container.dataset === false ? 15 : this.container.dataset.graphMarginBottom;
	this.barMarginTop = 'graphMarginTop' in this.container.dataset === false ? 15 : this.container.dataset.graphMarginTop;
	this.barHeight = 'graphHeight' in this.container.dataset === false ? 10 : this.container.dataset.graphHeight;

	this.values = this.container.dataset.graphValues.split(',');
	this.highestValue = this.getHighestValue(this.values);
	this.valueType = 'valueType' in this.container.dataset ? '%' : this.container.dataset.graphValueType;

	this.colors = 'graphValuesColors' in this.container.dataset === false ? [] : this.container.dataset.graphValuesColors.split(',');
	this.classes = 'graphValuesClasses' in this.container.dataset === false ? [] : this.container.dataset.graphValuesClasses.split(',');

	this.graphDefaultColor = 'graphDefaultColor' in this.container.dataset === false ? '#F2F2F2' : this.container.dataset.graphDefaultColor;
	this.graphDefaultClass = 'graphDefaultClass' in this.container.dataset === false ? '#F2F2F2' : this.container.dataset.graphDefaultClass;

	this.labels = 'graphLabels' in this.container.dataset === false ? [] : this.container.dataset.graphLabels.split(',');
	this.longestLabel = this.findLongestLabel(this.labels);

	this.barOffsetLeft = Math.round((this.longestLabel.length * this.barHeight) + this.barHeight / 2);

	this.graphEnd = 'graphEnd' in this.container.dataset === false ? 0 : parseFloat(this.container.dataset.graphEnd);
	this.graphEndColor = 'graphEndColor' in this.container.dataset === false ? 0 : this.container.dataset.graphEndColor;
	this.graphOversize = 'graphOversize' in this.container.dataset === false ? 0 : parseFloat(this.container.dataset.graphOversize);

	this.graphValueBehindBar = 'graphValueBehindBar' in this.container.dataset === false ? true : ( this.container.dataset.graphValueBehindBar === '1' );

	this.toAnimate = [];

	this.snap = new Snap(this.container);

	this.initialize();
}

SdGraph.prototype.initialize = function() {
	this.container.innerHTML = '';

	this.containerWidth = this.container.getBoundingClientRect().width;
	this.containerHeight = this.container.getBoundingClientRect().height;
	this.posY = this.barMarginTop;
	this.containerWidth = this.graphOversize !== 0 && this.graphOversize > 100 ? this.containerWidth / this.graphOversize * 100 : this.containerWidth;

	this.setSvgSize();

	this.values.forEach((value, index) => {
		value = parseFloat(value);
		let color = this.colors[index] ? this.colors[index] : this.graphDefaultColor;
		let cssClass = this.classes[index] ? this.classes[index] : this.graphDefaultClass;
		let label = this.labels[index] ? this.labels[index] : '';
		let labelPosY = this.posY === this.barMarginTop ? this.barMarginTop + this.barHeight : (this.posY + this.barHeight);

		// Label Text
		let text = this.snap.text(0, labelPosY, label);
		text.attr('class', 'graph-bar-label');

		// Bar
		let rect = this.snap.rect(this.barOffsetLeft, this.posY, 0, this.barHeight);
		rect.attr('fill', color);
		rect.attr('class', cssClass);
		this.toAnimate.push({
			element: rect,
			value: 'width',
			to: this.calculateValue(value, this.containerWidth - this.barOffsetLeft)
		});

		// Value Text
		let textPosX;
		if ( this.graphValueBehindBar === true )
			textPosX = this.barOffsetLeft + this.calculateValue(value, this.containerWidth - this.barOffsetLeft) + this.barMarginBottom;
		else
			textPosX = this.barOffsetLeft + this.calculateValue(this.highestValue, this.containerWidth - this.barOffsetLeft) + this.barMarginBottom;
		let valueText = this.snap.text(this.barOffsetLeft, labelPosY, value.toLocaleString('de-DE'));
		valueText.attr('class', 'graph-bar-value')
		this.toAnimate.push({
			element: valueText,
			value: 'x',
			to: textPosX
		});

		this.posY = this.posY + this.barMarginTop + this.barHeight + this.barMarginBottom
	});

	// End-Line
	let endlineX = this.barOffsetLeft + this.calculateValue(this.graphEnd, this.containerWidth - this.barOffsetLeft);
	let endLine = this.snap.line(endlineX, 0, endlineX, this.containerHeight);
	endLine.attr('style', 'stroke: ' + this.graphEndColor);
	endLine.attr('class', 'graph-end-line');

	// Animation start, if graph comes into viewport
	let observer = new IntersectionObserver(entries => {
		entries.forEach(entry => {
			if ( entry.isIntersecting ) {
				this.toAnimate.forEach(animateConf => {
					let conf = {};
					conf[animateConf.value] = animateConf.to;
					animateConf.element.animate(conf, this.animationDuration);
				});
			}
		});
	}, {
		root: null,
		rootMargin: '0px',
		threshold: [0.75]
	});
	observer.observe(this.container);
}

SdGraph.prototype.calculateValue = function(value, width) {
	let calculatedValue;
	switch ( this.valueType ) {
		case '%':
			calculatedValue = this.calculatePercentValue(value, width);
			break;
		case 'decimal':
			calculatedValue = this.calculateDecimalValue(value, width, this.highestValue);
			break;
	}
	return calculatedValue;
}

SdGraph.prototype.calculatePercentValue = function(value, width) {
	return (width / 100) * value;
}

SdGraph.prototype.calculateDecimalValue = function(value, width, highestValue) {
	return (width / (highestValue * 1.2)) * value;
}

SdGraph.prototype.findLongestLabel = function(labels) {
	return labels.reduce((longest, current) => {
		return longest.length > current.length ? longest : current;
	});
}

SdGraph.prototype.getHighestValue = function(values) {
	return values.reduce((highest, current) => {
		return highest > current ? highest : current;
	});
}

SdGraph.prototype.setSvgSize = function() {
	const containerWidth = this.container.parentElement.getBoundingClientRect().width;
	this.container.setAttribute('viewBox', '0 0 ' + containerWidth + ' ' + this.values.length * (this.barMarginBottom + this.barHeight + this.barMarginTop));
	this.container.setAttribute('height', this.values.length * (this.barMarginBottom + this.barHeight + this.barMarginTop));
}

export { SdGraph };
